/**
 * Reports API Service
 */

import { api } from './api';
import type { ProjectReportResponse, GlobalReportResponse, ReportQueryParams } from '../types/api';

export type ExportJobStatus =
  | 'processing'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface ExportJobSummary {
  job_id: string;
  project_id: string;
  status: ExportJobStatus;
  requested_by: string;
  created_at: string;
  updated_at: string;
  total_scenarios: number;
  scenarios_processed: number;
  filters?: {
    domain?: string[];
    area?: string[];
    status?: string[];
    tester?: string[];
  };
  error_message?: string;
  download_url?: string;
}

export interface ExportListResponse {
  exports: ExportJobSummary[];
  count: number;
  scope: 'project' | 'global';
  project_id?: string;
}

export const reportsApi = {
  /**
   * Get comprehensive project report with optional server-side filtering & pagination
   */
  getProjectReport: async (
    projectId: string,
    params?: ReportQueryParams,
  ): Promise<ProjectReportResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.page != null) queryParams.set('page', params.page.toString());
    if (params?.page_size != null) queryParams.set('page_size', params.page_size.toString());
    // Multi-value filters use repeated keys (?domain=A&domain=B) so that
    // individual values containing commas survive round-trip intact.
    for (const v of params?.domain ?? []) queryParams.append('domain', v);
    for (const v of params?.area ?? []) queryParams.append('area', v);
    for (const v of params?.tester ?? []) queryParams.append('tester', v);
    for (const v of params?.status ?? []) queryParams.append('status', v);
    if (params?.has_issues != null) queryParams.set('has_issues', params.has_issues.toString());
    if (params?.schedule) queryParams.set('schedule', params.schedule);
    if (params?.date_from) queryParams.set('date_from', params.date_from);
    if (params?.date_to) queryParams.set('date_to', params.date_to);

    const query = queryParams.toString();
    const endpoint = query
      ? `/reports/project/${projectId}?${query}`
      : `/reports/project/${projectId}`;

    return api.get<ProjectReportResponse>(endpoint);
  },

  /**
   * Start async CSV export for a project report
   */
  startCsvExport: async (
    projectId: string,
    filters?: {
      domain?: string[];
      area?: string[];
      status?: string[];
      tester?: string[];
    },
  ): Promise<{ job_id: string; status: string }> => {
    return api.post<{ job_id: string; status: string }>(
      `/reports/project/${projectId}/export-csv`,
      filters ? { filters } : undefined,
    );
  },

  /**
   * Get CSV export job status
   */
  getCsvExportStatus: async (
    jobId: string,
  ): Promise<{
    job_id: string;
    status: string;
    download_url?: string;
    total_scenarios: number;
    scenarios_processed: number;
    error_message?: string;
  }> => {
    return api.get(`/reports/export-status/${jobId}`);
  },

  /**
   * Cancel an in-flight CSV export job. Returns the resulting status —
   * may be 'cancelled' (success), or the already-terminal state if the
   * worker finished in the same moment.
   */
  cancelCsvExport: async (
    jobId: string,
  ): Promise<{ job_id: string; status: ExportJobStatus; message?: string }> => {
    return api.delete(`/reports/export-status/${jobId}`);
  },

  /**
   * List previous CSV exports for a project (newest first).
   * Completed items include a fresh 1 h presigned download URL.
   */
  listProjectExports: async (
    projectId: string,
    limit = 50,
  ): Promise<ExportListResponse> => {
    return api.get(`/reports/project/${projectId}/exports?limit=${limit}`);
  },

  /**
   * List recent CSV exports across all projects (admin global view).
   */
  listGlobalExports: async (limit = 50): Promise<ExportListResponse> => {
    return api.get(`/reports/exports?limit=${limit}`);
  },

  /**
   * Get global reporting dashboard data
   */
  getGlobalReport: async (params?: {
    status?: 'all' | 'active' | 'inactive';
    days?: number;
  }): Promise<GlobalReportResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.set('status', params.status);
    if (params?.days) queryParams.set('days', params.days.toString());

    const query = queryParams.toString();
    const endpoint = query ? `/reports/global?${query}` : '/reports/global';

    return api.get<GlobalReportResponse>(endpoint);
  },
};
